import axios from 'axios';
import { authArgs, authConfig, getApiContext } from './requestAuth';
import { withReadPolicy } from './requestPolicy';

// Feature gates must fail closed within a bounded time, but this timeout stays
// local to the small entitlement request so uploads and AI operations are not
// affected by it.
export const SUBSCRIPTION_CONFIRM_TIMEOUT_MS = 8000;

export const getSubscriptionStatus = async (orgId) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.get(`${apiUrl}/api/subscription/${orgId}/status`, withReadPolicy(authConfig(token)));
  return res.data;
};

// Authoritative entitlement check used by feature gates. The backend verifies
// that the requested organization matches the authenticated session.
export const confirmSubscription = async (orgId, { signal } = {}) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.get(`${apiUrl}/api/subscription/${orgId}/confirm`, {
    ...authConfig(token),
    signal,
    timeout: SUBSCRIPTION_CONFIRM_TIMEOUT_MS,
  });
  return res.data;
};

// Requests with an AbortSignal belong to their caller; do not share their
// cancellation with another mounted menu. Other reads share only within the
// same API/organization/credential context.
const pendingSubscriptionDetails = new Map();

export const getSubscriptionDetails = async (orgId, { signal, token: tokenOverride } = {}) => {
  const context = getApiContext();
  const token = tokenOverride === undefined ? context.token : tokenOverride;
  const { apiUrl } = context;
  const config = withReadPolicy(authConfig(token));
  if (signal) {
    const response = await axios.get(`${apiUrl}/api/subscription/${orgId}/details`, { ...config, signal });
    return response.data;
  }
  const key = JSON.stringify([apiUrl, orgId, token]);
  if (pendingSubscriptionDetails.has(key)) return pendingSubscriptionDetails.get(key);
  const request = axios.get(`${apiUrl}/api/subscription/${orgId}/details`, config)
    .then((res) => res.data)
    .finally(() => pendingSubscriptionDetails.delete(key));
  pendingSubscriptionDetails.set(key, request);
  return request;
};

export const addSubscriptionUsers = async (orgId, users) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.post(`${apiUrl}/api/subscription/users?org_id=${orgId}`, { users }, ...authArgs(token));
  return res.data;
};

export const listSubscriptionUsers = async (orgId) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.get(`${apiUrl}/api/subscription/${orgId}/users`, withReadPolicy(authConfig(token)));
  return res.data;
};

export const updateSubscriptionUser = async (userId, status) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.put(`${apiUrl}/api/subscription/users/${userId}?status=${status}`, ...authArgs(token));
  return res.data;
};

export const getOrganization = async (orgId) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.get(`${apiUrl}/api/organizations/${orgId}/`, withReadPolicy(authConfig(token)));
  return res.data;
};

export const updateOrganization = async (orgId, data) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.put(`${apiUrl}/api/organizations/${orgId}/`, data, ...authArgs(token));
  return res.data;
};

export const createUpgradeSubscription = async (orgId, email, interval, numUsers, opts = {}) => {
  const { apiUrl, token } = getApiContext();
  const res = await axios.post(`${apiUrl}/razorpay/create-upgrade-subscription`, {
    org_id: orgId,
    email,
    interval,
    num_users: numUsers,
    plan: opts.plan || "business",
    coupon_code: opts.coupon_code || "",
    payment_method: opts.payment_method || "card",
    post_tracking_token: opts.post_tracking_token || null,
  }, authConfig(token));
  return res.data;
};
